import { useState } from "react";
import {
  Card,
  CardContent,
  Box,
  Button,
  Switch,
  Typography,
  Alert,
  InputAdornment,
} from "@mui/material";

import { motion } from "framer-motion";
import { toast } from "react-toastify";
import apiService from "../../../../../../service/Api";
import CustomTextField from "../../../../../../components/common/Textfield";
import GoFundMeWidget from "../../../../../../components/GoFundMeWidget";
import DonationsTable from "../../components/chema/DonationsTable";


interface DonationsTabProps {
  streamData: {
    go_fund_me_link?: string;
    go_fund_me_enabled: boolean;
  } | null;
  streamId: string;
  isAdmin: boolean;
  isMobile: boolean;
  fetchStreamDetails: () => Promise<void>;
  donationRefreshKey?: number;
}

export default function DonationsTab({
  streamData,
  streamId,
  isAdmin,
  isMobile,
  fetchStreamDetails,
  donationRefreshKey = 0,
}: DonationsTabProps) {

  const [goFundMeLink, setGoFundMeLink] = useState(
    streamData?.go_fund_me_link || ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSaveLink = async () => {
    if (!goFundMeLink || !goFundMeLink.includes("gofundme.com")) {
      setError("Please enter a valid GoFundMe URL");
      return;
    }

    setIsSaving(true);
    setError("");

    try {
      const payload = { go_fund_me_link: goFundMeLink };
      await apiService.patch(`/api/streams/stream/${streamId}/`, payload);
      toast.success("GoFundMe link saved successfully");
      await fetchStreamDetails();
    } catch (err: unknown) {
      console.error("Failed to save link:", err);
      let errorMessage = "Failed to save GoFundMe link";
      if (err instanceof Object && 'response' in err && typeof err.response === 'object' && err.response !== null && 'data' in err.response && typeof err.response.data === 'object' && err.response.data !== null && 'detail' in err.response.data) {
        errorMessage = (err.response.data as Record<string, unknown>).detail as string;
      }
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleGoFundMeEnabled = async (newValue: boolean) => {
    try {
      await apiService.patch(`/api/streams/stream/${streamId}/`, {
        go_fund_me_enabled: newValue,
      });
      toast.success(
        `Donations ${newValue ? "enabled" : "disabled"} successfully`
      );
      await fetchStreamDetails();
    } catch (err) {
      console.error("Failed to toggle donation setting:", err);
      toast.error("Failed to update donation setting.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ height: "100%", boxShadow: 0, borderRadius: "20px" }}>
        <CardContent>
          {/* Admin Section */}
          {isAdmin && (
            <Box sx={{ mb: 3 }}>
              <CustomTextField
                label="GoFundMe Embed URL"
                value={goFundMeLink}
                onChange={(e) => {
                  setGoFundMeLink(e.target.value);
                }}
                fullWidth
                margin="normal"
                placeholder="https://www.gofundme.com/f/campaign-name or widget URL"
                helperText="Enter the GoFundMe campaign URL or widget embed URL"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={handleSaveLink}
                        variant="contained"
                        size="small"
                        disabled={isSaving}
                      >
                        {isSaving
                          ? "Saving..."
                          : streamData?.go_fund_me_link
                          ? "Update"
                          : "Save"}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  my: 2,
                }}
              >
                <Typography sx={{ mr: 2 }}>Enable Donations</Typography>
                <Switch
                  checked={streamData?.go_fund_me_enabled || false}
                  onChange={(e) =>
                    handleToggleGoFundMeEnabled(e.target.checked)
                  }
                  color="primary"
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}

          {streamData?.go_fund_me_link && isMobile && (
            <Box sx={{ mt: 4 }}>
              <GoFundMeWidget url={streamData.go_fund_me_link} />
            </Box>
          )}

        </CardContent>
        {streamData?.go_fund_me_enabled && streamId && (
          <DonationsTable
            streamId={streamId}
            title="All Donations"
            ordering="-created_at"
            refreshKey={donationRefreshKey}
          />
        )}
      </Card>
    </motion.div>
  );
}